import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import type {Reservation, Reservee} from '@wix/ambassador-table-reservations-v1-reservation/types'
import type {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import type {Checkout} from '@wix/ambassador-checkout/types'
import type {CreateCartResponse} from '@wix/ambassador-ecom-v2-cart/types'

import {reservationsService} from '../../../services/reservationsService'
import {RequestStatus} from '../../../utils/wrapRequest'
import {goToReservationConfirmation, navigateToCheckoutPage} from '../../../utils/navigation'
import {getLogger} from '../../../utils/getLogger'
import {checkoutService} from '../../../services/checkoutService'
import {cartService} from '../../../services/cartService'
import {isFreeCheckout} from '../../../utils/ecom'
import {trackTableReservedEvent} from '../../../utils/trackEvent'
import {EXPERIMENTS, TransactionNames} from '../../../utils/constants'
import {isCaptchaCancelledError} from '../../../utils/captchaRetry'
import {isProtectionActive} from '../../../utils/cancellationPolicy'

import {isReservationSuccessful, getRefererFromUrlParams} from './utils'

// error codes for which we have separate modal titles
export enum ReserveReservationApplicationErrorCode {
  HELD_EXPIRED = 'HELD_EXPIRED',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export const reserveReservation = (flowAPI: ControllerFlowAPI) => {
  return async (
    reservation: Reservation,
    reservee: Reservee,
    reservationLocation: ReservationLocation,
    shouldRequestSkipCheckout: boolean,
  ) => {
    const reserveReservationTransaction = flowAPI.panoramaClient.transaction(
      TransactionNames.reserveReservation,
    )

    reserveReservationTransaction.start({
      params: {
        reservation,
        reservee,
        reservationLocation,
      },
    })

    const useCart = flowAPI.experiments.enabled(EXPERIMENTS.useCartInsteadOfCheckout)
    const setProps = flowAPI.controllerConfig.setProps
    const {getResolvedError} = flowAPI.errorHandler
    const logger = getLogger(flowAPI.bi!)

    try {
      setProps({submitReservationStatus: RequestStatus.LOADING})
      const finishedReservation = await reservationsService.reserveReservation(
        flowAPI,
        reservation.id ?? '',
        reservee,
        reservation.revision ?? '',
      )

      // this is the only place where can get just created reservation
      if (finishedReservation?.id) {
        const referer = getRefererFromUrlParams(flowAPI)

        logger.reservationCreated({
          isPreview: false,
          reservation: finishedReservation!,
          reservee,
          reservationLocation,
          isCardTokenization: false,
          referer,
        })

        if (useCart) {
          await handleCartFlow(
            flowAPI,
            finishedReservation,
            reservee,
            reservationLocation,
            shouldRequestSkipCheckout,
          )
        } else {
          await handleCheckoutFlow(
            flowAPI,
            finishedReservation,
            reservee,
            reservationLocation,
            reservation.details?.partySize,
          )
        }
      } else {
        throw new Error('No reservation')
      }
    } catch (err: any) {
      // User closed captcha dialog
      if (isCaptchaCancelledError(err)) {
        setProps({submitReservationStatus: RequestStatus.DEFAULT})

        return
      }

      flowAPI.panoramaClient.errorMonitor().reportError(err)

      const resolvedError = getResolvedError(err)

      setProps({
        submitReservationStatus: RequestStatus.FAILED,
        apiRequestErrorDetails: resolvedError,
      })
    }

    reserveReservationTransaction.finish()
  }
}

const handleCheckoutFlow = async (
  flowAPI: ControllerFlowAPI,
  finishedReservation: Reservation,
  reservee: Reservee,
  reservationLocation: ReservationLocation,
  partySize?: number | null,
) => {
  const {getResolvedError} = flowAPI.errorHandler
  let checkoutResponse: {checkout?: Checkout}

  try {
    checkoutResponse = await checkoutService.createCheckout(flowAPI, {
      reservationId: finishedReservation.id!,
      reservee,
      locationId: reservationLocation.location?.id ?? undefined,
    })
  } catch (error) {
    getResolvedError(error)
    if (isReservationSuccessful(finishedReservation)) {
      console.error('Checkout is undefined')

      await goToReservationConfirmation({
        flowAPI,
        reservationId: finishedReservation.id!,
      })

      return
    } else {
      throw error
    }
  }

  if (!checkoutResponse?.checkout) {
    throw new Error('Checkout is undefined')
  }

  const isFreeReservation = isFreeCheckout(checkoutResponse.checkout)

  if (
    isFreeReservation &&
    !isProtectionActive(
      reservationLocation.configuration?.cancellationPolicy,
      partySize ?? finishedReservation.details?.partySize,
    )
  ) {
    if (finishedReservation?.id) {
      trackTableReservedEvent(flowAPI, finishedReservation.id, reservee)
    }

    try {
      await checkoutService.createOrderFromCheckout(flowAPI, checkoutResponse.checkout!.id!)
    } catch (error) {
      if (isReservationSuccessful(finishedReservation)) {
        console.error('Error creating order from checkout', error)
      } else {
        throw error
      }
    }
    await goToReservationConfirmation({
      flowAPI,
      reservationId: finishedReservation.id!,
    })
  } else {
    await navigateToCheckoutPage(flowAPI, checkoutResponse.checkout!.id!, finishedReservation.id!)
  }
}

const handleCartFlow = async (
  flowAPI: ControllerFlowAPI,
  finishedReservation: Reservation,
  reservee: Reservee,
  reservationLocation: ReservationLocation,
  shouldRequestSkipCheckout: boolean,
) => {
  const {getResolvedError} = flowAPI.errorHandler
  const createCartTransaction = flowAPI.panoramaClient.transaction(TransactionNames.createCart)

  let cartResponse: CreateCartResponse

  const createCartParams = {
    reservationId: finishedReservation.id!,
    reservee,
    locationId: reservationLocation.location?.id ?? undefined,
  }

  createCartTransaction.start({params: createCartParams})

  try {
    cartResponse = await cartService.createCart(flowAPI, createCartParams)
  } catch (error) {
    flowAPI.panoramaClient.errorMonitor().reportError(error as Error)
    getResolvedError(error)
    if (isReservationSuccessful(finishedReservation)) {
      console.error('Cart is undefined')

      await goToReservationConfirmation({
        flowAPI,
        reservationId: finishedReservation.id!,
      })

      return
    } else {
      throw error
    }
  }

  createCartTransaction.finish({cart: cartResponse})

  if (!cartResponse?.cart) {
    throw new Error('Cart is undefined')
  }

  await navigateToCheckoutPage(flowAPI, cartResponse.cart!.id!, finishedReservation.id!, {
    skipCheckout: shouldRequestSkipCheckout,
  })
}
